import styled from "@emotion/styled";
import React from "react";
import { PlanOfCareItemId, RequiredDutySheetTask, VisitInstanceId } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { undefinedToNull } from "../../../utils/undefinedToNull";
import { Center } from "../../Center";
import { AppList } from "../../Generic/AppList";
import { If } from "../../Generic/If";
import NotesText from "../../Patient/components/NotesText";
import { SelectedDutyTasks } from "../useTimesheetForm";
import MedflytDutyTaskItem from "./MedflytDutyTaskItem";
import { HashMap } from "../../../utils/HashMap";
import { DutiesPartition } from "../../../clockInOutUtils";

interface MedflytDutyTasksListProps {
  activeVisitId: VisitInstanceId;
  duties: DutiesPartition;
  isTaskItemDisabled?: boolean;
  selectedDutyTasks: SelectedDutyTasks;
  approvedDutiesMap: HashMap<PlanOfCareItemId, boolean | null>;
  colorRequiredTasks: boolean;
  onApproveDuty: (duty: PlanOfCareItemId, approval: boolean) => void;
  onChangeNotes: (taskId: PlanOfCareItemId, notes: string) => void;
  onCheck: (taskId: PlanOfCareItemId, isChecked: boolean) => void;
  onDisabledTaskItemClick?: () => void;
}

function MedflytDutyTasksList(props: MedflytDutyTasksListProps) {
  const shouldColorDuty = (duty: RequiredDutySheetTask) => {
    return (
      props.colorRequiredTasks &&
      duty.isRequired &&
      (!props.selectedDutyTasks.has(duty.id) || isDutyMissingNotes(duty))
    );
  };
  const isDutyMissingNotes = (duty: RequiredDutySheetTask) => {
    return (
      props.selectedDutyTasks.has(duty.id) &&
      props.approvedDutiesMap.get(duty.id) === false &&
      props.selectedDutyTasks.get(duty.id)?.notes === null
    );
  };
  return (
    <MedflytDutyTasksList.Root>
      <If condition={props.duties.optional.length() === 0 && props.duties.required.length() === 0}>
        <NotesText>
          <Center>{translate(STRINGS.THIS_VISIT_DOES_NOT_REQUIRE_ANY_TASKS_TO_BE_DONE)}</Center>
        </NotesText>
      </If>
      <MedflytDutyTasksList.PillWrapper>
        <MedflytDutyTasksList.DutiesPill required={true}>
          {`${props.duties.required.length()} ${translate(STRINGS.REQUIRED_TASKS)}`}
        </MedflytDutyTasksList.DutiesPill>
      </MedflytDutyTasksList.PillWrapper>
      {props.duties.required.map((task) => (
        <MedflytDutyTaskItem
          key={`task-${task.id}-${task.code}`}
          duty={task}
          activeVisitId={props.activeVisitId}
          colored={shouldColorDuty(task)}
          isApproved={undefinedToNull(props.approvedDutiesMap.get(task.id))}
          onApproveDuty={props.onApproveDuty}
          notes={props.selectedDutyTasks.get(task.id)?.notes ?? null}
          isChecked={props.selectedDutyTasks.has(task.id)}
          isRequired={task.isRequired}
          isTaskItemDisabled={props.isTaskItemDisabled}
          onChangeNotes={props.onChangeNotes}
          onCheck={props.onCheck}
          onDisabledTaskItemClick={props.onDisabledTaskItemClick}
        />
      ))}
      <MedflytDutyTasksList.PillWrapper>
        <MedflytDutyTasksList.DutiesPill required={false}>
          {`${props.duties.optional.length()} ${translate(STRINGS.OPTIONAL_TASKS)}`}
        </MedflytDutyTasksList.DutiesPill>
      </MedflytDutyTasksList.PillWrapper>
      {props.duties.optional.map((task) => (
        <MedflytDutyTaskItem
          key={`task-${task.id}-${task.code}`}
          duty={task}
          activeVisitId={props.activeVisitId}
          colored={shouldColorDuty(task)}
          isApproved={undefinedToNull(props.approvedDutiesMap.get(task.id))}
          onApproveDuty={props.onApproveDuty}
          notes={props.selectedDutyTasks.get(task.id)?.notes ?? null}
          isChecked={props.selectedDutyTasks.has(task.id)}
          isRequired={task.isRequired}
          isTaskItemDisabled={props.isTaskItemDisabled}
          onChangeNotes={props.onChangeNotes}
          onCheck={props.onCheck}
          onDisabledTaskItemClick={props.onDisabledTaskItemClick}
        />
      ))}
    </MedflytDutyTasksList.Root>
  );
}

MedflytDutyTasksList.Root = styled(AppList)`
  margin-bottom: 20px;

  > .AppListItem + .AppListItem {
    margin-top: 8px;
  }

  & > .AppListItem {
    padding: 0;
  }
`;
MedflytDutyTasksList.PillWrapper = styled.div`
  border-bottom: 1px solid var(--gray-50);
  display: flex;
  align-items: center;
  justify-content: center;
`;

MedflytDutyTasksList.DutiesPill = styled.div<{ required: boolean }>`
  padding: 4px 12px;
  margin: 12px;
  border-radius: 24px;
  font-weight: 600;
  font-size: 16px;
  background-color: ${(props) => (props.required ? "var(--tw-red-100)" : "var(--tw-slate-100)")};
`;

export default MedflytDutyTasksList;
